import { useMemo, useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Box, Theme, Typography, makeStyles } from '@material-ui/core';

import EditRateRecCategoryDrawer from './EditRateRecCategoryDrawer';

import { useContainers } from '../../../../hooks';
import { useRateRecCategories } from '../../../../hooks/useRateRecCategories';
import { Row } from '../../../../models';
import LoadingSpinner from '../../../LoadingSpinner';
import TableWithComment from '../../../TableWithComment';

interface StyleProps {
  rateRecCategories: number;
}
interface RateRecCategory {
  rateRecCategoryId: string;
  rateRecCategoryName: string;
  adjustments?: unknown[];
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  categoryTableContainer: {
    paddingLeft: theme.spacing(1.42),
    paddingRight: theme.spacing(1.42),
    paddingTop: theme.spacing(0)
  },
  categoryTable: {
    '& .MuiTable-root': {
      '& .MuiTableBody-root .MuiTableRow-root:first-child .MuiTableCell-root': {
        borderTop: `1px solid ${theme.palette.divider}`
      },
      '& .MuiTableCell-root.MuiTableCell-head': {
        fontFamily: theme.typography.fontFamily,
        '&.MuiTableCell-stickyHeader': {
          paddingTop: '0px',
          '&:nth-child(2), &:nth-child(3)': {
            paddingBottom: '0px'
          }
        }
      },
      '& .MuiTableCell-root.MuiTableCell-body': {
        backgroundColor: theme.palette.common.white,
        borderBottom: ({ rateRecCategories }) =>
          rateRecCategories > 0 ? `1px solid ${theme.palette.divider}` : 'none',
        borderRight: 'none',
        paddingTop: theme.spacing(0.42),
        paddingBottom: theme.spacing(0.42),
        fontFamily: theme.typography.fontFamily
      }
    }
  },
  noCategoriesMessage: {
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.h3.fontFamily,
    fontSize: theme.typography.h3.fontSize,
    letterSpacing: theme.typography.h3.letterSpacing,
    display: 'flex',
    justifyContent: 'center'
  },
  dialogActions: {
    marginTop: '20px',
    justifyContent: 'space-between',
    '& [data-testid="cancelBtn"]': {
      color: theme.palette.primary.main
    },
    '& [data-testid="confirmBtn"]': {
      height: '36px',
      width: '96px'
    },
    '& button': {
      fontWeight: 'bold'
    }
  },
  dialogTitle: {
    display: 'inline-block',
    color: theme.palette.text.primary,
    fontFamily: theme.typography.h3.fontFamily,
    fontSize: theme.typography.h2.fontSize,
    padding: '0px 20px 10px 0px',
    marginTop: '-6px',
    verticalAlign: 'middle'
  },
  dialogTitleBlock: {
    justifyContent: 'space-between',
    display: 'flex'
  },
  dialog: {
    fontFamily: theme.typography.h3.fontFamily,
    '& [role="dialog"]': {
      padding: '23px 24px 24px 24px',
      height: '241px',
      width: '437px',
      borderRadius: '4px',
      lineHeight: '22px'
    }
  },
  dialogContent: {
    width: '383px',
    height: '66px',
    padding: '0',
    fontSize: theme.typography.h3.fontSize,
    fontFamily: theme.typography.fontFamily,
    paddingLeft: '5px',
    paddingRight: '1px',
    margin: 'auto'
  },
  inlineBlock: {
    display: 'inline-block'
  },
  titlePadding: {
    padding: '10px 20px 10px 20px'
  }
}));

const RateRecCategories = () => {
  const { rateRecCategories, isLoading, updateData: hookUpdateData } = useRateRecCategories();
  const [isEditCategoryDrawerOpen, setIsEditCategoryDrawerOpen] = useState<boolean>(false);
  const [currentRow, setCurrentRow]: any = useState({});
  const { currentContainer } = useContainers();
  const classes = useStyles({ rateRecCategories: rateRecCategories.length });
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('Rate Rec Category'),
        width: '23%',
        filterable: false,
        sortable: false,
        placeholder: t('Category'),
        renderCell: (row: Row) => row.name
      }
    ],
    [t]
  );

  const rows = useMemo(
    () =>
      (rateRecCategories as RateRecCategory[])
        .map((rc) => ({
          id: rc.rateRecCategoryId,
          name: rc.rateRecCategoryName,
          adjustments: rc.adjustments ?? []
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rateRecCategories]
  );

  const toggleEditCategoryDrawer = (row: Row) => {
    setCurrentRow(row);
    setIsEditCategoryDrawerOpen((isOpen) => !isOpen);
  };

  const handleCloseEditCategoryDrawer = useCallback(() => {
    setIsEditCategoryDrawerOpen(false);
  }, []);

  const updateData = useCallback(
    async (id: string, adjustments: unknown) => {
      await hookUpdateData(id, adjustments);
    },
    [hookUpdateData]
  );

  return isLoading ? (
    <LoadingSpinner />
  ) : (
    <Box className={classes.categoryTableContainer}>
      <EditRateRecCategoryDrawer
        isOpen={isEditCategoryDrawerOpen}
        selectedRateRecCategory={currentRow}
        rateRecCategories={rateRecCategories}
        currentContainer={currentContainer}
        updateData={updateData}
        onClose={handleCloseEditCategoryDrawer}
      />
      <TableWithComment
        isRateRecCategories
        displayCategoryActions
        className={classes.categoryTable}
        columns={columns}
        rows={rows}
        toggleEditCategoryDrawer={toggleEditCategoryDrawer}
      />
      {rateRecCategories.length === 0 && (
        <Typography className={classes.noCategoriesMessage}>
          There are no Rate Reconciliation Categories to display.
        </Typography>
      )}
    </Box>
  );
};

export default RateRecCategories;
